import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Eye } from "lucide-react";
import { Badge } from "@repo/ui/badge";
import { Skeleton } from "@repo/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { useDebounce } from "@/hooks/useDebounce";
import { fetchProperties } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";

const PREVIEW_LIMIT = 20;

export interface ExportPreviewFilters {
  postcodes?: string[];
  state?: string;
  adapterSlug?: string;
  fromDate?: string;
  toDate?: string;
}

interface ExportPreviewTableProps {
  filters: ExportPreviewFilters;
}

const priceFormatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

const truncateUrl = (url: string, max = 48) =>
  url.length <= max ? url : `${url.slice(0, max)}…`;

export function ExportPreviewTable({ filters }: ExportPreviewTableProps) {
  const debounced = useDebounce(filters, 400);

  const { data, isFetching, isError } = useQuery({
    queryKey: ["export-preview", debounced],
    queryFn: async () => {
      const result = await fetchProperties({
        filters: {
          postcodes: debounced.postcodes,
          state: debounced.state,
        },
        limit: PREVIEW_LIMIT,
        sortBy: "last_scraped_at",
        sortOrder: "desc",
      });
      console.log("[ExportPreview] fetch", {
        count: result.data.length,
        total: result.count,
        filters: debounced,
      });
      return result;
    },
    staleTime: 30_000,
  });

  const rows = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <div className="card-elevated p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Preview</h2>
          <Badge variant="outline" className="text-xs">
            top {PREVIEW_LIMIT}
          </Badge>
          {!isFetching && (
            <span className="text-xs text-muted-foreground">
              {totalCount.toLocaleString()} match
              {totalCount === 1 ? "" : "es"}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Live preview from the property database. Reflects postcode + state
          filters only.
        </p>
      </div>

      {isError ? (
        <div className="text-xs text-destructive py-4 text-center">
          Failed to load preview
        </div>
      ) : isFetching && rows.length === 0 ? (
        <div className="space-y-1.5">
          {["a", "b", "c", "d", "e"].map((k) => (
            <Skeleton key={k} className="h-8 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 text-center">
          No properties match these filters
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Suburb</TableHead>
              <TableHead className="text-xs">Postcode</TableHead>
              <TableHead className="text-xs">Address</TableHead>
              <TableHead className="text-xs text-right">Sold Price</TableHead>
              <TableHead className="text-xs">Sold Date</TableHead>
              <TableHead className="text-xs">Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const sourceUrl = row.source_urls?.[0] ?? null;
              return (
                <TableRow key={row.id}>
                  <TableCell className="text-xs">
                    {row.suburb}{" "}
                    <span className="text-muted-foreground">{row.state}</span>
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {row.postcode || "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.full_address}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {row.last_sale_price
                      ? priceFormatter.format(row.last_sale_price)
                      : row.price_display || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.last_sale_date
                      ? formatDate(row.last_sale_date)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {sourceUrl ? (
                      <a
                        href={sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline font-mono"
                        title={sourceUrl}
                      >
                        {truncateUrl(sourceUrl)}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
